import { createContext, ReactNode, useCallback, useMemo } from 'react';

// project import
import defaultConfig from '@app/config';

// types
import {
  CustomizationProps,
  FontFamily,
  I18n,
  MenuOrientation,
  PresetColor,
  ThemeDirection,
  ThemeMode,
} from '@app/types/config';
import useLocalStorage from '@hooks/useLocalStorage';

// initial state
const initialState: CustomizationProps = {
  ...defaultConfig,
  onChangeMenuOrientation: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeLocale: () => {},
  onChangeDirection: () => {},
  onChangeContainer: () => {},
  onChangeFontFamily: () => {},
  onChangeBorderRadius: () => {},
  onChangeOutlinedField: () => {},
  onReset: () => {},
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useLocalStorage('berry-config-vite-ts', {
    menuOrientation: initialState.menuOrientation,
    miniDrawer: initialState.miniDrawer,
    fontFamily: initialState.fontFamily,
    borderRadius: initialState.borderRadius,
    outlinedFilled: initialState.outlinedFilled,
    mode: initialState.mode,
    presetColor: initialState.presetColor,
    i18n: initialState.i18n,
    themeDirection: initialState.themeDirection,
    container: initialState.container,
  });

  const onChangeMenuOrientation = useCallback(
    (menuOrientation: MenuOrientation) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        menuOrientation,
      }));
    },
    [setConfig]
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer: boolean) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        miniDrawer,
      }));
    },
    [setConfig]
  );

  const onChangeMode = useCallback(
    (mode: ThemeMode) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        mode,
      }));
    },
    [setConfig]
  );

  const onChangePresetColor = useCallback(
    (presetColor: PresetColor) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        presetColor,
      }));
    },
    [setConfig]
  );

  const onChangeLocale = useCallback(
    (i18n: I18n) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        i18n,
      }));
    },
    [setConfig]
  );

  const onChangeDirection = useCallback(
    (themeDirection: ThemeDirection) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        themeDirection,
      }));
    },
    [setConfig]
  );

  const onChangeContainer = useCallback(
    (container: boolean) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        container,
      }));
    },
    [setConfig]
  );

  const onChangeFontFamily = useCallback(
    (fontFamily: FontFamily) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        fontFamily,
      }));
    },
    [setConfig]
  );

  const onChangeBorderRadius = useCallback(
    (_event: Event, newValue: number | number[]) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        borderRadius: newValue as number,
      }));
    },
    [setConfig]
  );

  const onChangeOutlinedField = useCallback(
    (outlinedFilled: boolean) => {
      setConfig((prevConfig: any) => ({
        ...prevConfig,
        outlinedFilled,
      }));
    },
    [setConfig]
  );

  const onReset = useCallback(() => {
    setConfig({ ...defaultConfig });
  }, [setConfig]);

  const contextValue = useMemo(
    () => ({
      ...config,
      onChangeMenuOrientation,
      onChangeMiniDrawer,
      onChangeMode,
      onChangePresetColor,
      onChangeLocale,
      onChangeDirection,
      onChangeContainer,
      onChangeFontFamily,
      onChangeBorderRadius,
      onChangeOutlinedField,
      onReset,
    }),
    [
      config,
      onChangeMenuOrientation,
      onChangeMiniDrawer,
      onChangeMode,
      onChangePresetColor,
      onChangeLocale,
      onChangeDirection,
      onChangeContainer,
      onChangeFontFamily,
      onChangeBorderRadius,
      onChangeOutlinedField,
      onReset,
    ]
  );

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
